<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1100" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>
      <v-card-text>
        <div class="mb-4">
          <label class="label-input">Section Name</label>
          <v-text-field v-model="fields.title" hide-details />
        </div>

        <SectionLayout :settings="fields.settings" :container-page="fields.containerPage" />

        <div class="mb-5">
          <label class="label-input">Image</label>
          <div class="group-item">
            <UploadImage
              :image="{ imageSrc: fields.image }"
              :height="`200`"
              :typeUpload="'image'"
              @callback="obj => (fields.image = obj.imageSrc)"
              @callbackDelete="fields.image = ''"
            />
            <label class="mt-4 d-block">Image position</label>
            <v-radio-group v-model="fields.mediaPosition" row hide-details class="mt-1">
              <v-radio label="Left" value="left" />
              <v-radio label="Right" value="right" />
            </v-radio-group>
          </div>
        </div>

        <div class="mb-5">
          <label class="label-input">Image Title</label>
          <div class="group-item">
            <ColorPicker
              label="Background Color"
              :value="fields.bgMediaTitle"
              name="cefloreco-benefis-bg-media-title"
              default-color="#1099dc"
              @input="value => (fields.bgMediaTitle = value)"
            />
            <label class="mt-4 d-block">Content</label>
            <ckeditor :editor="editor" v-model="fields.mediaTitle" :config="editorConfig" />
          </div>
        </div>

        <div class="mb-5">
          <label class="label-input">Title & Description</label>
          <div class="group-item">
            <ckeditor :editor="editor" v-model="fields.titleContent" :config="editorConfig" />
          </div>
        </div>

        <div class="mb-5">
          <div class="d-flex justify-space-between align-center mb-2">
            <label class="label-input mb-0">Benefits (max {{ MAX_BENEFIT_ITEMS }})</label>
            <v-btn small color="primary" :disabled="fields.items.length >= MAX_BENEFIT_ITEMS" @click="handleAddItem">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add Benefit</span>
            </v-btn>
          </div>
          <div class="group-item mb-3">
            <v-row>
              <v-col cols="12" md="6">
                <ColorPicker
                  label="Highlight Color"
                  :value="fields.colorHighlightItem"
                  name="cefloreco-benefis-color-highlight"
                  default-color="#ff7657"
                  @input="value => (fields.colorHighlightItem = value)"
                />
              </v-col>
            </v-row>
          </div>
          <div class="group-item">
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(item, index) in fields.items" :key="`benefit-item-${index}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center" style="width: 100%">
                    <span>Benefit {{ index + 1 }}</span>
                    <v-btn
                      icon
                      :disabled="fields.items.length <= MIN_BENEFIT_ITEMS"
                      @click.stop="handleRemoveItem(index)"
                    >
                      <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <label>Number</label>
                  <v-text-field v-model="item.number" hide-details class="mt-0 mb-3" />
                  <label>Content</label>
                  <ckeditor :editor="editor" v-model="item.content" :config="editorConfig" />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>
      </v-card-text>
      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { cloneDeep } from 'lodash';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { updateHtmlFromData } from '../common/html/update';
import UploadImage from '../common/form/upload-file/upload-image.vue';
import SectionLayout from '../common/ui-modules/section-layout/editor.vue';
import ColorPicker from '../common/ui-modules/inputs/color-picker.vue';

const MAX_BENEFIT_ITEMS = 6;
const MIN_BENEFIT_ITEMS = 1;

const buildItemDefault = () => ({
  number: '01',
  content: '<h3>Product strategy</h3><p>Audience insight, positioning and a clear path to value.</p>',
});

const getDefaultFields = () => ({
  title: 'Cefloreco Benefis Section',
  settings: {
    paddingTop: true,
    paddingBottom: false,
    backgroundColor: '#FFFFFF',
    textColor: '#000000',
    overlayColor: '#000000',
    overlayOpacity: 0,
    showOverlay: false,
    backgroundImage: '',
    useCustomWidth: true,
    customWidth: 1204,
  },
  containerPage: {
    enableStyle: false,
    background: '#FFFFFF',
  },
  image: '',
  mediaPosition: 'left',
  mediaTitle: '<p>IDEA TO</p><h3>IMPACT</h3>',
  bgMediaTitle: '#1099dc',
  titleContent: '<p>What we do</p><h2>One team. Every part of the product.</h2><p>We move from insight to launch.</p>',
  colorHighlightItem: '#ff7657',
  items: [buildItemDefault()],
});

export default {
  name: 'CeflorecoBenefisSectionEditor',
  components: { UploadImage, SectionLayout, ColorPicker },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: getDefaultFields(),
      MAX_BENEFIT_ITEMS,
      MIN_BENEFIT_ITEMS,
    };
  },
  mounted() {
    const def = getDefaultFields();
    const extract = cloneDeep(this.sectionEditor.sectionExtract || {});

    this.fields = {
      ...def,
      ...extract,
      settings: { ...def.settings, ...(extract.settings || {}) },
      containerPage: { ...def.containerPage, ...(extract.containerPage || {}) },
      items: extract.items && extract.items.length ? extract.items.slice(0, MAX_BENEFIT_ITEMS) : def.items,
    };
  },
  methods: {
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionCeflorecoBenefis(this.sectionEditor.section.html, {
        ...this.fields,
        items: (this.fields.items || []).slice(0, MAX_BENEFIT_ITEMS),
      });
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleAddItem() {
      if (this.fields.items.length >= MAX_BENEFIT_ITEMS) return;
      const next = buildItemDefault();
      next.number = String(this.fields.items.length + 1).padStart(2, '0');
      this.fields.items.push(next);
    },
    handleRemoveItem(index) {
      if (this.fields.items.length <= MIN_BENEFIT_ITEMS) return;
      this.fields.items.splice(index, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.group-item {
  border: 1px solid #0000000f;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}
.ck-editor__editable_inline,
.ck-editor__editable {
  min-height: 140px;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
